import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { X, Loader2, ArrowRight, PartyPopper, Check } from 'lucide-react';
import { saveMemberAttribute, completeWelcomeSurvey } from '../lib/api';
import { getAvailablePoints } from '../lib/points';
import { useMe } from '../hooks/useProfile';
import type { WelcomeSurveyQuestion } from '@swipeopinion/shared';

interface Props {
  questions: WelcomeSurveyQuestion[];
  points: number;
  onClose: () => void;
  /** Fired after the success screen's primary CTA (e.g. parent routes on). */
  onTakeSurvey: () => void;
}

/**
 * Guaranteed first win (T9). Walks a member through a few demand-ranked
 * profiling questions (real enrichment), then credits a flat instant bonus and
 * shows progress toward the first reward. Always qualifies — the member can
 * skip any question and still earn.
 */
export default function WelcomeSurveyModal({ questions, points, onClose, onTakeSurvey }: Props) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [single, setSingle] = useState<string | null>(null);
  const [multi, setMulti] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [awarded, setAwarded] = useState<number | null>(null);

  const q = questions[idx];
  const isMulti = q?.questionType === 'multi_select';
  const isLikert = q?.questionType === 'likert';
  const isLast = idx >= questions.length - 1;
  const hasAnswer = isMulti ? multi.length > 0 : !!single;

  async function finish(finalAnswers: Record<string, string>) {
    setSubmitting(true);
    setError(false);
    try {
      // Persist answers as explicit profile data (reuses the enrichment path:
      // source='explicit' + profile-tier re-eval). Best-effort per write.
      await Promise.all(
        Object.entries(finalAnswers).map(([k, v]) => saveMemberAttribute(k, v).catch(() => {})),
      );
      const res = await completeWelcomeSurvey();
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['me'] }),
        qc.invalidateQueries({ queryKey: ['surveys'] }),
      ]);
      setAwarded(res.points_awarded || points);
    } catch {
      setError(true);
    }
    setSubmitting(false);
  }

  function handleNext(skip: boolean) {
    const updated = { ...answers };
    if (!skip && q) {
      const val = isMulti ? multi.join(',') : single;
      if (val) updated[q.attributeKey] = val;
    }
    setAnswers(updated);
    setSingle(null);
    setMulti([]);
    if (isLast || questions.length === 0) void finish(updated);
    else setIdx((i) => i + 1);
  }

  function toggleMulti(value: string) {
    if (value === 'none') {
      setMulti((m) => (m.includes('none') ? [] : ['none']));
      return;
    }
    setMulti((m) => {
      const without = m.filter((v) => v !== 'none');
      return without.includes(value) ? without.filter((v) => v !== value) : [...without, value];
    });
  }

  // ── Success ────────────────────────────────────────────────────────────────
  if (awarded !== null) {
    const trustLevel = (me as { trust_level?: number } | undefined)?.trust_level ?? 1;
    const minPts = (me as { redemption_minimum_points?: number } | undefined)?.redemption_minimum_points
      ?? (trustLevel >= 2 ? 500 : 1000);
    const toGo = Math.max(0, minPts - getAvailablePoints(me));
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="welcome-survey-modal">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6 text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-[#E6F7EE] flex items-center justify-center mx-auto">
            <PartyPopper className="w-7 h-7 text-[#10B981]" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#111827]">Your first reward is in!</h3>
            <p className="inline-flex items-center gap-2 bg-[#10B981]/10 text-[#10B981] font-bold text-lg px-5 py-2 rounded-full mt-2">
              +{awarded} pts
            </p>
          </div>
          {toGo > 0 ? (
            <p className="text-sm text-[#6B7280]">
              <span className="font-semibold text-[#111827]">{toGo.toLocaleString()} pts to go</span> to your first cash-out — just a few more surveys.
            </p>
          ) : (
            <p className="text-sm text-[#6B7280]">You can cash out now — head to rewards when you&apos;re ready.</p>
          )}
          <div className="space-y-2 pt-1">
            <button
              onClick={onTakeSurvey}
              className="w-full py-3 rounded-full bg-[#5B4FE8] text-white text-sm font-semibold hover:bg-[#4338CA] transition-colors flex items-center justify-center gap-1.5"
              data-testid="welcome-take-survey"
            >
              Take a survey <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="w-full text-sm text-[#6B7280] hover:text-[#111827]">Maybe later</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Questions / confirm ─────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="welcome-survey-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6 space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold text-[#10B981]">Welcome survey · +{points} pts guaranteed</p>
            <h3 className="text-lg font-bold text-[#111827] mt-0.5">Quick questions to match you</h3>
          </div>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#111827]" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {questions.length > 0 && (
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#10B981] transition-all" style={{ width: `${Math.round(((idx) / questions.length) * 100)}%` }} />
          </div>
        )}

        {q ? (
          <div className="space-y-3">
            <p className="text-xs text-[#9CA3AF]">Question {idx + 1} of {questions.length}</p>
            <p className="text-sm font-medium text-[#111827]">{q.questionText}</p>

            {isLikert ? (
              <div className="space-y-2">
                <div className="grid grid-cols-5 gap-1.5">
                  {q.options.map((opt, i) => (
                    <button
                      key={opt.value}
                      onClick={() => setSingle(opt.value)}
                      aria-label={opt.label}
                      className={`py-3 rounded-lg border text-xs font-medium transition-colors ${
                        single === opt.value ? 'bg-[#5B4FE8] text-white border-[#5B4FE8]' : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-[11px] text-[#9CA3AF] px-0.5">
                  <span>{q.options[0]?.label}</span>
                  <span>{q.options[q.options.length - 1]?.label}</span>
                </div>
              </div>
            ) : isMulti ? (
              <div className="flex flex-wrap gap-2">
                {q.options.map((opt) => {
                  const checked = multi.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => toggleMulti(opt.value)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors flex items-center gap-1.5 ${
                        checked ? 'bg-[#EEF2FF] text-[#5B4FE8] border-[#5B4FE8]' : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]'
                      }`}
                    >
                      <span className={`w-4 h-4 rounded border flex items-center justify-center ${checked ? 'bg-[#5B4FE8] border-[#5B4FE8] text-white' : 'border-[#D1D5DB]'}`}>
                        {checked ? <Check className="w-3 h-3" /> : ''}
                      </span>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {q.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSingle(opt.value)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      single === opt.value ? 'bg-[#5B4FE8] text-white border-[#5B4FE8]' : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-[#6B7280]">Your profile already looks great — claim your welcome bonus below.</p>
        )}

        {error && (
          <p className="text-sm text-[#DC2626] bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
            Something went wrong — please try again.
          </p>
        )}

        <div className="space-y-2">
          <button
            onClick={() => handleNext(false)}
            disabled={(!!q && !hasAnswer) || submitting}
            className="w-full py-2.5 rounded-full bg-[#5B4FE8] text-white text-sm font-semibold disabled:opacity-40 hover:bg-[#4338CA] transition-colors flex items-center justify-center gap-1.5"
            data-testid="welcome-continue"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {!q ? `Claim +${points} pts` : isLast ? `Finish · +${points} pts` : 'Continue'}
          </button>
          {q && (
            <button
              onClick={() => handleNext(true)}
              disabled={submitting}
              className="block w-full text-center text-xs text-gray-400 hover:text-gray-600"
            >
              Skip this question
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
